/**
 * 胜率引擎的公共类型。见 docs/ARCHITECTURE.md §10.2。
 *
 * 为什么类型单独成文件而不是塞进 index.ts：planner / enumerate / montecarlo
 * 都要引用这些类型，而 index.ts 要 import 它们三个——类型放 index 会形成
 * 模块环。环在 ESM 下不一定崩，但会让 `const` 初始化顺序变成运气问题。
 *
 * 安全边界（§10.1，红线相关）：本模块的输入**只有**自己的底牌、已公开的公共牌、
 * 未弃牌的对手数量、以及「对全场亮出」的 publicDead。面朝下 muck 的牌绝不能进
 * publicDead——那等于把弃牌信息喂给还在局里的玩家。
 */
import type { CardId } from '../ids.js'
import type { DeckVariant } from '../poker/types.js'
import type { OpponentModel } from './ranges.js'

/** 街。与 poker/types.ts 的 Street 同形，但胜率引擎不依赖 HandState，故独立定义。 */
export type EquityStage = 'preflop' | 'flop' | 'turn' | 'river'

/** 只算未弃牌的对手；0 表示所有人已弃牌（直接返回 equity=1）。 */
export const MAX_OPPONENTS = 8

export const DEFAULT_MAX_MILLIS = 800
export const DEFAULT_TARGET_ITERATIONS = 40_000

export interface EquityRequest {
  readonly requestId: string
  readonly hole: readonly [CardId, CardId]
  /** 已公开的公共牌，长度只能是 0 / 3 / 4 / 5 */
  readonly board: readonly CardId[]
  /** 1..8，只算未弃牌的；0 表示无人可与你争池 */
  readonly opponents: number
  readonly model: OpponentModel
  /** 只允许放「对全场公开亮出」的牌（§10.1 第 2 条） */
  readonly publicDead?: readonly CardId[]
  readonly maxMillis?: number
  readonly targetIterations?: number
  readonly allowExact?: boolean
  /** 同一 (hole, board, opponents) 复用同一 seed 可消除数字抖动 */
  readonly seed?: number
  /** 玩法。短牌：牌池只有 6-A，同花大于葫芦，A-6-7-8-9 是顺子。缺省 = 标准 */
  readonly deckVariant?: DeckVariant
}

/**
 * `model` 字段是**口径标签**，UI 必须显示它（§10.5 第 1 条：不允许只显示裸百分比）。
 * 'weightedCombos' 是规格里没有列出的第三种口径，见 deviations。
 */
export type EquityModelLabel = 'uniformRandomOpponents' | 'topPercent' | 'weightedCombos'

export interface EquityResult {
  readonly requestId: string
  /** 三者之和恒为 1（容差 1e-9） */
  readonly win: number
  readonly tie: number
  readonly lose: number
  /** win + Σ(平分时 1/k)。这才是「胜率」的正确数学定义，UI 显示的也是它。 */
  readonly equity: number
  readonly iterations: number
  readonly exact: boolean
  /** 精确枚举时为 0；蒙特卡洛时对齐展示量 equity（不是 win 的二项标准误） */
  readonly stdErr: number
  readonly elapsedMs: number
  readonly stage: EquityStage
  readonly method: 'exact' | 'montecarlo'
  readonly done: boolean
  readonly degradedSamples?: number
  readonly model: EquityModelLabel
  /** 翻前查表命中（iterations=0）。规格没有这个字段，但把「查表」伪装成蒙特卡洛会让调用方误判精度。 */
  readonly fromTable?: boolean
}

/**
 * 枚举 / 模拟的原始计数。
 *
 * `deals` 与 `weight` 分开：精确枚举在带权范围（top / weighted）下，
 * 每个发牌组合的权重不是 1，分母必须用权重和而不是组合数，否则
 * 边界手牌类的部分权重会被算成整份。
 */
export interface Tally {
  /** 实际评估过的发牌组合数（上报为 iterations） */
  deals: number
  /** 权重总和（均匀范围时等于 deals） */
  weight: number
  win: number
  tie: number
  lose: number
  /** Σ(单次 equity × 权重)，单次 equity 为 1 / 1/k / 0 */
  equitySum: number
  /** Σ(单次 equity² × 权重)，用于样本方差 */
  equitySqSum: number
  /** 采样碰撞超过重试上限、退化为均匀取牌的次数 */
  degraded: number
}

export function emptyTally(): Tally {
  return { deals: 0, weight: 0, win: 0, tie: 0, lose: 0, equitySum: 0, equitySqSum: 0, degraded: 0 }
}

/**
 * 单调时钟。浏览器与 Node 都有 performance.now，但 shared 的 lib 里既没有 DOM
 * 也没有 node 类型（它要同时打进两端），所以从 globalThis 上探测而不是直接引用。
 */
const PERF = (globalThis as { performance?: { now(): number } }).performance

export function nowMs(): number {
  return PERF ? PERF.now() : Date.now()
}
